import { createApi } from '@reduxjs/toolkit/dist/query/react';
import baseQueryWithReauth from 'client/util/baseQueryWithReauth';
import qs from 'qs';

const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
export const surveyAnalyzeApiSlice = createApi({
  reducerPath: 'surveyAnalyzeReducer',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SurveyAnalyze'],
  endpoints: builder => ({
    analyzeEvolution: builder.query({
      query: ({ survey, ...rest }) => ({
        url: `/survey-analyze/${survey}/?${qs.stringify({ ...rest, timeZone })}`,
        method: 'GET',
      }),
      providesTags: ['SurveyAnalyze'],
      transformResponse: (response, _, arg) => {
        const toBucket = (key, values, keyAt) => ({
          key: new Date(key * 3600000),
          ...values.reduce((acc, [average, score, positive, neutral, negative], idx) => {
            acc[keyAt(idx)] = {
              average,
              score,
              positive,
              neutral,
              negative,
              total: positive + neutral + negative,
            };
            return acc;
          }, {}),
        });

        // Breakdown response: { breakdown, series: [{key,label}], buckets }.
        if (!Array.isArray(response)) {
          const series = response.series || [];
          return {
            series,
            breakdown: response.breakdown,
            rows: (response.buckets || []).map(([key, values]) =>
              toBucket(key, values, idx => series[idx].key),
            ),
          };
        }

        // Series-comparison response: rows keyed by the requested filter ids.
        const filters = arg.filters || ['all'];
        return {
          series: null,
          breakdown: null,
          rows: response.map(([key, values]) => toBucket(key, values, idx => filters[idx])),
        };
      },
    }),
    getSnapshot: builder.query({
      query: ({ survey, since, until, saved_search_id }) => ({
        url: `/surveys/${survey}/snapshot`,
        params: { since, until, saved_search_id },
      }),
      providesTags: ['SurveyAnalyze'],
    }),
    getBreakdown: builder.query({
      query: ({ survey, since, until, saved_search_id, dimension, property_key }) => ({
        url: `/surveys/${survey}/breakdown`,
        params: { since, until, saved_search_id, dimension, property_key },
      }),
      providesTags: ['SurveyAnalyze'],
    }),
    getFollowUpQuestions: builder.query({
      query: ({ survey, since, until, saved_search_id }) => ({
        url: `/surveys/${survey}/follow-up-questions`,
        params: { since, until, saved_search_id },
      }),
      providesTags: ['SurveyAnalyze'],
    }),
  }),
});

export const {
  useAnalyzeEvolutionQuery,
  useGetSnapshotQuery,
  useGetBreakdownQuery,
  useGetFollowUpQuestionsQuery,
  reducer: surveyAnalyzeReducer,
} = surveyAnalyzeApiSlice;
